/**
 * Watchlist size limits, measured rather than guessed.
 *
 * These were placeholders — 100 keywords and 2,000 characters, set before any
 * backend limit was known. There isn't one anywhere near that. Against
 * production, using the desk's own keywords rather than synthetic ones:
 *
 *   headlines   500 keywords (7,929 chars)  119ms
 *   headlines 1,000 keywords (17,745 chars)  64ms
 *   firehose    500 keywords                 36ms
 *
 * The headlines endpoint caps `limit` at 100 but says nothing about the size
 * of `search_term`. So 500 is generous headroom over anything a person builds
 * by hand, and still an order of magnitude below where either feed strains.
 */
export const MAX_KEYWORDS_COUNT = 500;
export const MAX_KEYWORDS_TOTAL_CHARS = 10000;

/**
 * A ceiling on the whole watchlist, not just its keywords.
 *
 * Keywords were capped and tickers were not, which is backwards: a pasted book
 * of symbols is where the big numbers actually come from, and the S&P 500 list
 * carries 444 of them. This bounds the pair so one list cannot grow without
 * limit down the side that was never checked, while leaving room for an index
 * plus a decent set of topics.
 */
export const MAX_TERMS_TOTAL = 1000;

export interface KeywordsSizeValidation {
  ok: boolean;
  count: number;
  totalChars: number;
  countExceeded: boolean;
  charsExceeded: boolean;
  error?: string;
}

export const getKeywordsSize = (keywords: string[]): { count: number; totalChars: number } => {
  const count = keywords.length;
  const totalChars = keywords.reduce((sum, k) => sum + k.length, 0);
  return { count, totalChars };
};

export const validateKeywordsSize = (
  keywords: string[],
  limits: { maxCount?: number; maxChars?: number } = {}
): KeywordsSizeValidation => {
  const maxCount = limits.maxCount ?? MAX_KEYWORDS_COUNT;
  const maxChars = limits.maxChars ?? MAX_KEYWORDS_TOTAL_CHARS;
  const { count, totalChars } = getKeywordsSize(keywords);

  const countExceeded = count > maxCount;
  const charsExceeded = totalChars > maxChars;

  const errors: string[] = [];
  if (countExceeded) errors.push(`${count}/${maxCount} keywords`);
  if (charsExceeded) errors.push(`${totalChars}/${maxChars} characters`);

  return {
    ok: !countExceeded && !charsExceeded,
    count,
    totalChars,
    countExceeded,
    charsExceeded,
    error: errors.length > 0 ? `Keyword limit exceeded: ${errors.join(", ")}.` : undefined,
  };
};

export interface TermsTotalValidation {
  ok: boolean;
  total: number;
  error?: string;
}

/**
 * The whole watchlist against MAX_TERMS_TOTAL.
 *
 * Counts tickers and keywords together, because a term costs the same either
 * way and the split is our storage detail — a list of 900 symbols and one of
 * 900 phrases are the same size to every feed that reads them.
 */
export const validateTermsTotal = (
  tickers: string[],
  keywords: string[],
  maxTotal: number = MAX_TERMS_TOTAL
): TermsTotalValidation => {
  const total = tickers.length + keywords.length;
  return {
    ok: total <= maxTotal,
    total,
    error:
      total > maxTotal
        ? `This watchlist has ${total} entries. The limit is ${maxTotal} — split it into two lists and use both.`
        : undefined,
  };
};
